// Kind packs (spec v3 §1.3): a named set of kinds plus the navigation they add.
// `core` is implicit. A workspace lists the packs it enables in spec.packs.
import type { LucideIcon } from 'lucide-react'
import type { AnyKind } from '@/kinds/kind'
import type { NavSection } from '@/lib/nav'

export interface NavItem {
  to: string
  label: string
  /** glyph fallback, for a surface that cannot draw the line icon */
  icon: string
  /** line icon, drawn like the core items' */
  Icon?: LucideIcon
  /** filled from the pack's layout */
  section?: NavSection
  /** `g` + key jumps here on desktop */
  key?: string
  /** active only on an exact path match (a pack's first page when its others sit under it) */
  exact?: boolean
}

export interface Pack {
  name: string
  title: string
  description: string
  kinds: AnyKind[]
  nav: NavItem[]
  /**
   * How the pack sits in the sidebar and on the phone (a workspace's explicit
   * `spec.nav` is a hand-set list and still wins over `hides`).
   */
  layout?: PackLayout
}

export interface PackLayout {
  /** the sidebar section its items live in (default Connected) */
  section: NavSection
  /** shown above Plan when the workspace enables the pack (default: after Pages) */
  first?: boolean
  /** core and fixed nav keys the pack hides, in a workspace that enables it and has no explicit `nav` */
  hides?: string[]
  /** other nav keys that move into the section */
  adopts?: string[]
  /** keys (a pack item's `to`, or an adopted key) in display order; the rest follow */
  order?: string[]
}

const packs = new Map<string, Pack>()

export const registerPack = (p: Pack) => {
  packs.set(p.name, p)
  return p
}

export const getPack = (name: string) => packs.get(name)
export const allPacks = () => [...packs.values()]

/** Navigation contributed by the packs a workspace enables. */
export const navForPacks = (names: string[]): NavItem[] =>
  names.flatMap((n) => {
    const p = packs.get(n)
    return (p?.nav ?? []).map((i) => ({ ...i, section: p?.layout?.section }))
  })

/** The enabled packs themselves, for their layout. */
export const packsFor = (names: string[]): Pack[] => names.flatMap((n) => packs.get(n) ?? [])
